import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import classNames from "classnames";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { PlusIcon } from "@icons";
import { RequiredAccountRow } from "../requiredAccountsMapping";
import styles from "../RequiredAccountsMapping.module.scss";
import { RequiredAccountsCallbacks } from "./RequiredAccountAssignedCell";

export const RequiredAccountNameCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, RequiredAccountRow, unknown>) => {
        const { role, requiredAccountName, canCreateDefaultAccount } =
            row.original;
        const { callbacks } = useViewTableMeta<
            RequiredAccountsCallbacks,
            never
        >(table);
        const createLabel = `Create "${requiredAccountName}"`;

        return (
            <ViewTableCell captureClicks className={styles.textCell}>
                <HStack
                    gap={8}
                    align="center"
                    className={styles.requiredAccountRow}
                >
                    <BreakableText
                        text={requiredAccountName}
                        showTooltip
                        className={classNames(
                            styles.requiredAccount,
                            styles.requiredAccountText,
                        )}
                    />
                    {canCreateDefaultAccount && (
                        <Tooltip content={createLabel}>
                            <Button
                                variant="secondary"
                                size="sm"
                                iconOnly
                                aria-label={createLabel}
                                onClick={() =>
                                    callbacks?.onCreateDefaultAccount({ role })
                                }
                            >
                                <PlusIcon />
                            </Button>
                        </Tooltip>
                    )}
                </HStack>
            </ViewTableCell>
        );
    },
);

RequiredAccountNameCell.displayName = "RequiredAccountNameCell";
